import type { InternalPage } from '../lib/pages.ts'

/**
 * Every page in the internal webapp, in the order the rail shows them.
 *
 * This file belongs to the apps. It sits under `src/routes/` rather than in
 * `src/lib/` because every page ever added to this repo had to edit the list,
 * and a page should touch its own tree rather than the framework's. The shape
 * and the grouping logic stay in `src/lib/pages.ts`, which is framework.
 *
 * The `-` prefix is TanStack Router's ignore prefix, so this file adds no
 * route. Add an entry when you add a route under `src/routes/<app>/`, and
 * `-nav.test.ts` fails if you forget.
 *
 * **Every page carries a group, and the group matches the first segment of
 * the path.** A reader navigates `/payments/bin-mcc-fee-report` and looks for it
 * under Payments; a rail that listed it flat would answer a question the URL
 * had already answered differently. `-nav.test.ts` holds the two halves in
 * step, so a page added under a new app folder fails until it names a group.
 *
 * `GET /viewer-access` decides which entries the signed-in employee sees.
 * They star the pages they use, and the rail lists those first (ADR-0014).
 * A pinned page stays in the Tools foot.
 * The backend also protects each restricted page's API data.
 *
 * Every entry carries a `description`: one sentence on what the reader sees
 * and the question the page answers. The landing page prints it, and page
 * search matches it.
 */
export const INTERNAL_PAGES: readonly InternalPage[] = Object.freeze([
  {
    id: 'cs-scorecard',
    path: '/business-performance/client-scorecard',
    title: 'Client scorecard',
    description: 'Active merchants and processed payment counts by month.',
    group: 'Business performance'
  },
  {
    id: 'cs-payment-origin',
    path: '/business-performance/payment-origin',
    title: 'Payment origin',
    description: 'Payment origin shares and changes from a year earlier.',
    group: 'Business performance'
  },
  {
    id: 'feature-adoption',
    path: '/business-performance/feature-adoption',
    title: 'Feature adoption',
    description: 'Merchant feature configuration and paid usage counts.',
    group: 'Business performance'
  },
  {
    id: 'dms-volume',
    path: '/business-performance/dms-volume',
    takesMerchants: true,
    title: 'DMS volume',
    keywords: ['TPV', 'TCV'],
    description:
      'Processed payment volume and transaction counts by DMS bucket, for complete months or Finance weeks. A merchant with no integration type uses the HubSpot company DMS.',
    group: 'Business performance'
  },
  {
    id: 'tpv-tcv-peaks',
    path: '/business-performance/tpv-tcv-peaks',
    takesDateRange: true,
    takesMerchants: true,
    title: 'TPV / TCV peaks',
    keywords: ['volume', 'transactions'],
    description:
      'The highest payment-volume days since November 2024, and the merchants behind those peak days.',
    group: 'Business performance'
  },
  {
    id: 'weekly-trends',
    path: '/business-performance/weekly-trends',
    takesMerchants: true,
    title: 'Weekly trends',
    keywords: ['TPV', 'TCV', 'volume'],
    description:
      'Payment volume, transaction counts and active dealers by Finance week, Saturday through Friday.',
    group: 'Business performance'
  },
  {
    id: 'go-live-ramp-up',
    path: '/business-performance/go-live-ramp-up',
    takesMerchants: true,
    title: 'Go-live ramp-up',
    keywords: ['volume', 'ramp status'],
    description:
      "How each recently live merchant's processed volume compares with the volume it projected, by ramp status, and the cohort's volume by weeks since go-live.",
    group: 'Business performance'
  },
  {
    id: 'merchant-weekly-tiers',
    path: '/business-performance/merchant-weekly-tiers',
    takesMerchants: true,
    title: 'Merchant weekly tiers',
    keywords: ['TPV', 'volume'],
    description:
      'Each merchant’s TPV over the last completed weeks and the average-TPV tier it falls in, with the TPV of each tier week by week.',
    group: 'Business performance'
  },
  {
    id: 'platform-activity',
    path: '/business-performance/platform-activity',
    title: 'Platform activity',
    description:
      'Payment and merchant sign-in activity by weekday and hour, with quiet maintenance windows.',
    group: 'Business performance'
  },
  {
    id: 'hubspot-sync',
    path: '/merchants/hubspot-sync',
    title: 'HubSpot vs BlytzPay',
    description:
      'Merchants where HubSpot and BlytzPay disagree on status, DMS, go-live date or missing links, from external-tier HubSpot data.',
    group: 'Merchants'
  },
  {
    id: 'location-lookup',
    path: '/merchants/location-lookup',
    title: 'Location lookup',
    keywords: ['org', 'organization', 'merchant', 'dealer', 'pkid'],
    description:
      'Every merchant location beside its organization, searchable by either pkid or by name, including inactive organizations and ones with no location.',
    group: 'Merchants'
  },
  {
    id: 'locations',
    path: '/merchants/locations',
    title: 'Active locations',
    keywords: ['dealer', 'location', 'DMS', 'paid volume'],
    description:
      'Merchant locations with a paid payment in the last 30 to 365 days, with their merchant, address, DMS, payments and paid volume.',
    group: 'Merchants'
  },
  {
    id: 'master-list',
    path: '/merchants/master-list',
    title: 'Master list',
    keywords: ['MID', 'dealer', 'location', 'PayFacto', 'Betzy'],
    description:
      'Every merchant processing account (MID) with its PayFacto sub-account and debit account, DMS, go-live, first transaction, location and processor flags, from the Betzy master list; DMS and go-live can come from external-tier HubSpot data.',
    group: 'Merchants'
  },
  {
    id: 'merchant-360',
    path: '/merchants/merchant-360',
    takesDateRange: true,
    takesMerchants: true,
    title: 'Merchant 360',
    description:
      'One merchant at a glance: identity and configuration, payments and their volume, invoices late or unpaid, unresolved payment-posting failures, autopay, registrations and messages, the active callouts with their rules, and links to every detailed report. Counts and volume only.',
    group: 'Merchants'
  },
  {
    id: 'mmpv',
    path: '/merchants/mmpv',
    title: 'MMPV Watch',
    description:
      'Merchants with a minimum monthly processing volume clause: each one’s monthly volume against its threshold, and who is at or below the alert line. The thresholds are a snapshot of the Betzy list. Volume only.',
    group: 'Merchants'
  },
  {
    id: 'portfolio-callouts',
    path: '/merchants/portfolio-callouts',
    takesDateRange: true,
    takesMerchants: true,
    title: 'Portfolio callouts',
    description:
      'Which merchants need attention and why: processor mismatches, HubSpot and BlytzPay disagreements, late and unpaid invoices, and unresolved payment-posting failures, each with the rule that fired. No fee signals.',
    group: 'Merchants'
  },
  {
    id: 'processors',
    path: '/merchants/processors',
    title: 'Processors',
    description:
      "Each merchant's processor setting beside the PayFacto, ProPay and dual-enabled accounts it really has, with its PayFacto sub-accounts and mismatches flagged.",
    group: 'Merchants'
  },
  {
    id: 'rewind',
    path: '/merchants/rewind',
    takesMerchants: true,
    title: 'BlytzPay Rewind',
    description:
      'The calendar year in review for each merchant: transactions, volume, customers, payment mix and busiest months.',
    group: 'Merchants'
  },
  {
    id: 'merchant-settings',
    path: '/merchants/merchant-settings',
    title: 'Merchant settings',
    description:
      "Every merchant's account settings and feature switches, as they stand today.",
    group: 'Merchants'
  },
  {
    id: 'onboarding-pipeline',
    path: '/merchants/onboarding-pipeline',
    title: 'Onboarding pipeline',
    description:
      'HubSpot onboarding tickets by stage, company, owner, and management system, with the monthly backlog.',
    group: 'Merchants'
  },
  {
    id: 'sales-scorecard',
    path: '/sales/scorecard',
    takesDateRange: true,
    title: 'Sales scorecard',
    railTitle: 'Scorecard',
    description:
      'The sales team’s pipeline, win rate and outreach for a period, one measure to a row. Counts only, no revenue and no per-rep figures. From HubSpot, an external source.',
    group: 'Sales'
  },
  {
    id: 'sales-pipeline',
    path: '/sales/pipeline',
    title: 'Sales pipeline',
    railTitle: 'Pipeline',
    description:
      'The deals open in the Sales Pipeline today, by stage, with how many are past their expected close and how many are expected to close this month and next. Deal counts only. From HubSpot, an external source.',
    group: 'Sales'
  },
  {
    id: 'sales-win-loss',
    path: '/sales/win-loss',
    takesDateRange: true,
    title: 'Sales win and loss',
    railTitle: 'Win and loss',
    description:
      'Deals won and lost, the win rate, how long a won deal took, how deals moved from stage to stage and why they were lost. Counts only. From HubSpot, an external source.',
    group: 'Sales'
  },
  {
    id: 'sales-activity',
    path: '/sales/activity',
    takesDateRange: true,
    title: 'Sales activity',
    railTitle: 'Activity',
    description:
      'The calls, emails, meetings and texts the sales team logged in HubSpot, by week, and how many meetings led to a deal. Team totals only, no per-rep figures. From HubSpot, an external source.',
    group: 'Sales'
  },
  {
    id: 'sales-book-onboarding',
    path: '/sales/book-onboarding',
    takesDateRange: true,
    title: 'Sales book and onboarding',
    railTitle: 'Book and onboarding',
    description:
      'The deals the team closed since 2025, where each is in onboarding and how many have gone live or are stuck. Deal counts only, no revenue, bounty or per-rep figures. From HubSpot, an external source.',
    group: 'Sales'
  },
  {
    id: 'bin-mcc-fee-report',
    path: '/payments/bin-mcc-fee-report',
    takesDateRange: true,
    takesMerchants: true,
    title: 'BIN / MCC fee report',
    description:
      'Amaryllis processing cost by card BIN and MCC, and the volume those cards processed.',
    group: 'Payments'
  },
  {
    id: 'multicard-usage',
    path: '/payments/multicard-usage',
    takesDateRange: true,
    takesMerchants: true,
    title: 'Multicard usage',
    description:
      'Payment items a customer split across more than one card, by merchant.',
    group: 'Payments'
  },
  {
    id: 'retry-analysis',
    path: '/payments/retry-analysis',
    takesDateRange: true,
    takesMerchants: true,
    title: 'Retry analysis',
    description:
      'First-attempt payment failures and their retries, with credit cards and Amex left out.',
    group: 'Payments'
  },
  {
    id: 'retry-analysis-tsys',
    path: '/payments/retry-analysis-tsys',
    takesDateRange: true,
    takesMerchants: true,
    title: 'Retry analysis TSYS',
    description:
      'Retry analysis on the TSYS decline codes that replace the NMI codes retried today.',
    group: 'Payments'
  },
  {
    id: 'payer-experience',
    path: '/payments/payer-experience',
    takesDateRange: true,
    takesMerchants: true,
    title: 'Payer experience',
    description:
      'Payment attempt outcomes by month, merchant, payment method, wallet and decline reason, with the success and decline rates, so you can see where payers fail to pay and why.',
    group: 'Payments'
  },
  {
    id: 'ai-decline-codes',
    path: '/payments/ai-decline-codes',
    takesDateRange: true,
    takesMerchants: true,
    title: 'AI decline codes',
    description:
      'Why payments fail and how they get paid: by decline code, how many were paid within seven days, how often the same code came back and which card paid, with the common code sequences and the codes seen each week.',
    group: 'Payments'
  },
  {
    id: 'ach-returns',
    path: '/ach/returns',
    takesDateRange: true,
    title: 'ACH returns',
    keywords: ['ACH', 'NACHA', 'R01', 'unauthorized'],
    railTitle: 'Returns',
    description:
      'ACH charges and returns by code for a period, and whether the unauthorized return rate is over the NACHA ceiling.',
    group: 'ACH'
  },
  {
    id: 'ach-volume-trends',
    path: '/ach/volume-trends',
    takesDateRange: true,
    takesMerchants: true,
    title: 'ACH volume trends',
    keywords: ['debits', 'returns', 'notices of change'],
    railTitle: 'Volume trends',
    description:
      'ACH debits processed, debits stopped before submission, returns and notices of change, by period and merchant.',
    group: 'ACH'
  },
  {
    id: 'ach-nacha-rates',
    path: '/ach/nacha-rates',
    takesDateRange: true,
    takesMerchants: true,
    title: 'ACH NACHA rates',
    keywords: ['returns', 'debits', 'R01', 'unauthorized', 'administrative'],
    railTitle: 'NACHA rates',
    description:
      'ACH return rates against the NACHA thresholds, for the portfolio and for each merchant.',
    group: 'ACH'
  },
  {
    id: 'ach-merchant-rejections',
    path: '/ach/merchant-rejections',
    takesDateRange: true,
    takesMerchants: true,
    title: 'ACH merchant rejections',
    keywords: ['debits', 'returns', 'reject rate'],
    railTitle: 'Merchant rejections',
    description:
      'For each merchant, the ACH debits originated, how many stayed successful and how many were later returned, with the reject rate.',
    group: 'ACH'
  },
  {
    id: 'ach-return-reasons',
    path: '/ach/return-reasons',
    takesDateRange: true,
    takesMerchants: true,
    title: 'ACH return reasons',
    keywords: ['NACHA', 'R01', 'returns'],
    railTitle: 'Return reasons',
    description:
      'ACH returns by NACHA reason code and merchant, with each code’s share of all returns.',
    group: 'ACH'
  },
  {
    id: 'ach-daily',
    path: '/ach/daily',
    takesDateRange: true,
    takesMerchants: true,
    title: 'ACH daily',
    keywords: ['NACHA', 'returns', 'declines'],
    railTitle: 'Daily',
    description:
      'Each day’s ACH attempts by merchant, with their status and the reason code on every return or decline: which merchants are seeing returns, and why.',
    group: 'ACH'
  },
  {
    id: 'ach-trends',
    path: '/ach/trends',
    takesDateRange: true,
    takesMerchants: true,
    title: 'ACH trends',
    keywords: ['returns', 'return rate'],
    railTitle: 'Trends',
    description:
      'Monthly ACH processed and returned counts and the return rate for each merchant, with a Blytz-wide trend line: is ACH performance getting better or worse.',
    group: 'ACH'
  },
  {
    id: 'ach-reason-codes',
    path: '/ach/reason-codes',
    takesDateRange: true,
    takesMerchants: true,
    title: 'ACH reason codes',
    keywords: ['NACHA', 'R01', 'returns', 'declines'],
    railTitle: 'Reason codes',
    description:
      'ACH declines and returns by month, merchant and reason code, with each R-code explained: which reason is moving and why ACH attempts are failing.',
    group: 'ACH'
  },
  {
    id: 'overview',
    path: '/risk/chargebacks-overview',
    takesDateRange: true,
    takesMerchants: true,
    title: 'Chargebacks overview',
    keywords: ['CB', 'disputes'],
    description:
      'Card chargebacks by merchant and month, with the count, the share of card sales, the amount disputed and how each dispute stands, so you can see which merchants carry the chargeback risk.',
    group: 'Risk & chargebacks'
  },
  {
    id: 'by-origin',
    path: '/risk/chargebacks-by-origin',
    takesDateRange: true,
    takesMerchants: true,
    title: 'Chargebacks by payment origin',
    keywords: ['CB', 'disputes', 'autopay', 'recurring'],
    description:
      'Card chargebacks grouped by how the payment was made, with how many were on a saved or recurring payment method and how they came out, to show which kinds of payment get charged back.',
    group: 'Risk & chargebacks'
  },
  {
    id: 'decline-by-mid',
    path: '/risk/decline-by-mid',
    takesDateRange: true,
    takesMerchants: true,
    title: 'Decline by MID',
    keywords: ['approval rate', 'decline rate', 'merchant ID', 'refund rate'],
    description:
      'Approved, declined and refunded charges for each merchant ID (MID) and period, with the success, decline and refund rates, replacing the Tableau Decline Rate and Blytz View workbooks.',
    group: 'Risk & chargebacks'
  },
  {
    id: 'decline-retries',
    path: '/risk/decline-retries',
    takesDateRange: true,
    takesMerchants: true,
    title: 'Decline retries',
    keywords: ['decline code', 'retry approval'],
    description:
      'Card declines by merchant and decline code, and whether the same card was tried again within 84 hours, how soon, and how often the retry was approved.',
    group: 'Risk & chargebacks'
  },
  {
    id: 'declines',
    path: '/risk/declines',
    takesDateRange: true,
    takesMerchants: true,
    title: 'Declines',
    keywords: ['decline rate', 'NACHA', 'returns'],
    description:
      'Card declines, ACH declines and ACH returns by month or week, merchant and code, with each rate and the NACHA administrative and unauthorized buckets against their limits.',
    group: 'Risk & chargebacks'
  },
  {
    id: 'merchant-profile',
    path: '/risk/merchant-profile',
    takesDateRange: true,
    takesMerchants: true,
    title: 'Merchant profile',
    keywords: ['TPV', 'volume', 'DMS'],
    description:
      'One row per merchant with its DMS, go-live, BlytzCollect status and accounts, and last week’s TPV and transactions against the week before, with outlier weeks flagged.',
    group: 'Risk & chargebacks'
  },
  {
    id: 'merchant-risk',
    path: '/risk/merchant-risk',
    takesMerchants: true,
    title: 'Merchant risk',
    keywords: [
      'approval rate',
      'decline rate',
      'chargeback rate',
      'refund rate',
      'disputes'
    ],
    description:
      'One row per merchant for a window of completed weeks: volume, approval, decline, refund and chargeback rates, tenure, the latest week-over-week change, and a risk score and tier.',
    group: 'Risk & chargebacks'
  },
  {
    id: 'transaction-scoring',
    path: '/risk/transaction-scoring',
    takesMerchants: true,
    title: 'Transaction scoring',
    description:
      'The Risk Scoring model on recent card and ACH sales: how many each merchant had flagged by tier, with their amount, alerts and highest score.',
    group: 'Risk & chargebacks'
  },
  {
    id: 'merchant-outliers',
    path: '/risk/merchant-outliers',
    takesMerchants: true,
    title: 'Merchant outliers',
    description:
      'Each merchant’s TPV in the last completed week against its own recent weeks, with the Tableau outlier status and the Risk Dashboard flag.',
    group: 'Risk & chargebacks'
  },
  {
    id: 'portfolio-risk-kpis',
    path: '/risk/portfolio-risk-kpis',
    takesMerchants: true,
    title: 'Portfolio risk KPIs',
    description:
      'Monthly card approval, refunds, ACH returns, chargebacks, and payment volume across the portfolio.',
    group: 'Risk & chargebacks'
  },
  {
    id: 'duplicate-ach',
    path: '/transaction-monitoring/duplicate-ach',
    takesDateRange: true,
    takesMerchants: true,
    title: 'Duplicate ACH',
    description:
      'Bank accounts used by more than one customer or merchant, counted by merchant: which merchants see one account shared across customers.',
    group: 'Transaction monitoring'
  },
  {
    id: 'duplicate-cards',
    path: '/transaction-monitoring/duplicate-cards',
    takesDateRange: true,
    takesMerchants: true,
    title: 'Duplicate cards',
    description:
      'Cards used by more than one customer or merchant, counted by merchant: which merchants see one card shared across customers.',
    group: 'Transaction monitoring'
  },
  {
    id: 'duplicate-payments',
    path: '/transaction-monitoring/duplicate-payments',
    takesDateRange: true,
    takesMerchants: true,
    title: 'Duplicate payments',
    description:
      'Repeated charges of one amount to one customer on one day, counted by day, merchant and payment origin.',
    group: 'Transaction monitoring'
  },
  {
    id: 'high-low-tickets',
    path: '/transaction-monitoring/high-low-tickets',
    takesDateRange: true,
    takesMerchants: true,
    title: 'High / low tickets',
    description:
      'Unusually large or small processed charges, counted by merchant, against each merchant’s own 8-week average.',
    group: 'Transaction monitoring'
  },
  {
    id: 'owner-payments',
    path: '/transaction-monitoring/owner-payments',
    takesDateRange: true,
    takesMerchants: true,
    title: 'Owner payments',
    description:
      'Charges paid in the name of one of the merchant’s own owners, counted by merchant and payment method.',
    group: 'Transaction monitoring'
  },
  {
    id: 'repeat-card-usage',
    path: '/transaction-monitoring/repeat-card-usage',
    takesDateRange: true,
    takesMerchants: true,
    title: 'Repeat card usage',
    description:
      'Card attempts, declines and block candidates, counted by card BIN: which BINs carry cards that keep trying and failing.',
    group: 'Transaction monitoring'
  },
  {
    id: 'same-day-repeats',
    path: '/transaction-monitoring/same-day-repeats',
    takesDateRange: true,
    takesMerchants: true,
    title: 'Same day repeats',
    description:
      'Cards used more than once on one day, counted by day and merchant.',
    group: 'Transaction monitoring'
  },
  {
    id: 'collections-scorecard',
    path: '/collections/scorecard',
    takesDateRange: true,
    takesMerchants: true,
    title: 'Collections scorecard',
    railTitle: 'Scorecard',
    description:
      "How late each merchant's customers pay the invoices due in the range, how many never do, and the autopay, texts, and BlytzCollect calls behind that.",
    group: 'Collections & AI'
  },
  {
    id: 'account-delinquency',
    path: '/collections/account-delinquency',
    takesMerchants: true,
    title: 'Account delinquency',
    description:
      'How many accounts each merchant has past due on the latest monthly snapshot, by days past due, and how the book moved over the last 14 months. Counts of accounts only.',
    group: 'Collections & AI'
  },
  {
    id: 'propensity-to-pay',
    path: '/collections/propensity-to-pay',
    title: 'Propensity to pay',
    description:
      'How likely one account is to pay today, this week, and over the next 30 days, and to promise in those 30 days.',
    group: 'Collections & AI'
  },
  {
    id: 'propensity-accuracy',
    path: '/collections/propensity-accuracy',
    takesDateRange: true,
    title: 'Propensity accuracy',
    description:
      "The daily job's pay scores for past-due accounts, beside what each account did in the next 30 days.",
    group: 'Collections & AI'
  },
  {
    id: 'blytzcollect-analytics',
    path: '/collections/blytzcollect-analytics',
    title: 'BlytzCollect analytics',
    description:
      'How the outbound collections AI agent did: calls, billed time, transfers, and whether those calls collected a payment.',
    group: 'Collections & AI'
  },
  {
    id: 'ai-decline-impact',
    path: '/collections/ai-decline-impact',
    title: 'AI decline impact',
    description:
      'Whether the AI decline texts got declined payments paid sooner or more often.',
    group: 'Collections & AI'
  },
  {
    id: 'ai-decline-journeys',
    path: '/collections/ai-decline-journeys',
    takesDateRange: true,
    title: 'AI decline customer journeys',
    description:
      "Each customer's first AI-code decline and their next four attempts, up to the payment.",
    group: 'Collections & AI'
  },
  {
    id: 'ai-texts',
    path: '/product/ai-texts',
    takesDateRange: true,
    takesMerchants: true,
    title: 'AI texts',
    keywords: ['AI text collector', 'promise', 'payment link', 'balance'],
    description:
      'Every action the AI text collector took — a payment link, a balance or a promise — with a count for each status and the texts it sent.',
    group: 'Product'
  },
  {
    id: 'decline-payment-journey',
    path: '/product/decline-payment-journey',
    takesDateRange: true,
    title: 'Decline payment journey',
    keywords: ['decline code', 'retry', 'recovery', 'failed payment'],
    description:
      "Each customer's first declined payment, at every merchant and for every decline code, and their next four attempts, up to the payment.",
    group: 'Product'
  },
  {
    id: 'product-requests',
    path: '/product/requests',
    title: 'Product requests',
    keywords: ['feature request', 'roadmap', 'request funnel', 'idea'],
    description:
      'Submit a feature request to Product, and follow every request through triage, scoring and the stage gates.',
    group: 'Product'
  },
  {
    id: 'product-request-triage',
    path: '/product/request-triage',
    title: 'Request triage',
    keywords: ['prioritize', 'rubric', 'score', 'stage gate'],
    description:
      "Product's ranked list of requests by value at stake, where each is scored, checked against its gate and moved.",
    group: 'Product'
  },
  {
    id: 'cs-tools-autopay-accounts',
    path: '/cs-tools/autopay-accounts',
    title: 'Autopay accounts',
    description:
      "The autopay configurations on one merchant's accounts, once you pick that merchant.",
    group: 'Customer support'
  },
  {
    id: 'cs-tools-merchant-notes',
    path: '/cs-tools/merchant-notes',
    title: 'Merchant notes',
    description:
      'What the team has recorded about a merchant, and a place for any employee to add a note.',
    group: 'Customer support'
  },
  {
    id: 'cs-tools-registrations',
    path: '/cs-tools/registrations',
    takesDateRange: true,
    takesMerchants: true,
    title: 'Registrations',
    description:
      'New customer accounts by merchant, by the day the customer accepted terms.',
    group: 'Customer support'
  },
  {
    id: 'payment-report',
    path: '/cs-tools/payment-report',
    takesDateRange: true,
    takesMerchants: true,
    title: 'Payment report',
    description:
      'Individual payments by merchant, by the day they were processed.',
    group: 'Customer support'
  },
  {
    id: 'message-report',
    path: '/cs-tools/message-report',
    takesDateRange: true,
    takesMerchants: true,
    title: 'Message report',
    description:
      "Every message sent to a merchant's customers, by batch, one by one, and in total.",
    group: 'Customer support'
  },
  {
    id: 'undelivered',
    path: '/cs-tools/undelivered',
    takesDateRange: true,
    takesMerchants: true,
    title: 'Undelivered',
    description:
      'Outbound texts the messaging vendor could not deliver, by the day they were sent.',
    group: 'Customer support'
  },
  {
    id: 'opt-outs',
    path: '/cs-tools/opt-outs',
    takesDateRange: true,
    takesMerchants: true,
    title: 'Opt-outs',
    description:
      'Customer communication opt-outs by merchant, by the day they last changed.',
    group: 'Customer support'
  },
  {
    id: 'reconciliation',
    path: '/cs-tools/reconciliation',
    takesDateRange: true,
    takesMerchants: true,
    title: 'Reconciliation',
    description:
      'Payment failures that are still waiting for a person to resolve them.',
    group: 'Customer support'
  },
  {
    id: 'customer-list',
    path: '/cs-tools/customer-list',
    title: 'Customer list',
    description:
      "Every customer on one merchant's active accounts, with their contact details, ready to download.",
    group: 'Customer support'
  },
  {
    id: 'merchant-users',
    path: '/cs-tools/merchant-users',
    title: 'Merchant users',
    description:
      'Every login one merchant has, and every invite to it that is not accepted yet: name, email, last sign-in and when the invite expires.',
    keywords: ['staff', 'logins', 'invites', 'portal users'],
    group: 'Customer support'
  },
  {
    id: 'opt-out-sources',
    path: '/cs-tools/opt-out-sources',
    takesDateRange: true,
    takesMerchants: true,
    title: 'Who opted out',
    description:
      'Where each opt-out came from (the customer, the merchant, a STOP text, a phone call) and which user made it, by merchant and channel. Counts only, from the action service log.',
    group: 'Customer support'
  },
  {
    id: 'twilio-replies',
    path: '/cs-tools/twilio-replies',
    takesDateRange: true,
    takesMerchants: true,
    title: 'Twilio replies',
    description:
      'The STOP and START texts customers send back, by merchant and day, and the opt-out texts BlytzPay never recorded because the customer is still opted in. Counts only.',
    group: 'Customer support'
  },
  {
    id: 'company-tv-board',
    path: '/company/tv-board',
    title: 'Office TV board',
    keywords: ['tv', 'office tv', 'wall display', 'lobby', 'company totals'],
    description:
      'Company totals for payments, AI calls, texts, states and emails, full screen in a loop for the office TV.',
    group: 'Company'
  },
  {
    id: 'display-video',
    path: '/display-video',
    title: 'YouTube video',
    keywords: ['youtube', 'video', 'tv'],
    description: 'A YouTube video loop for an office TV, with optional sound.',
    group: 'Display video',
    inRail: false,
    tvFullscreen: true
  },
  {
    id: 'agent-board',
    path: '/engineering/agent-board',
    title: 'The Mob',
    keywords: ['agents', 'claude', 'claude code', 'the mob', 'tv', 'sessions'],
    description:
      "Every engineer's Claude Code sessions, drawn as The Mob's pixel crew, live from each computer for the office TV.",
    group: 'Engineering'
  },
  {
    id: 'service-performance',
    path: '/engineering/service-performance',
    takesDateRange: true,
    title: 'Service performance',
    description:
      'Requests, error rates and latency for each Cloud Run deployment, from an hourly summary of its logs.',
    group: 'Engineering'
  },
  {
    id: 'unanswered-questions',
    path: '/engineering/unanswered-questions',
    title: 'Unanswered questions',
    description:
      'Grouped saved search questions, their counts, distinct asker counts, and redacted samples from Firestore.',
    group: 'Engineering',
    keywords: ['search gaps', 'unanswered', 'questions']
  },
  {
    id: 'studio-library',
    path: '/studio/library',
    title: 'Studio',
    description:
      'Build reports from datalake sources and decks from slides and report cards, saved to you and shared when you choose.',
    group: 'Studio',
    pinned: true,
    icon: 'layers'
  },
  {
    // Opens only from a link, because it needs a document id.
    id: 'studio-report',
    path: '/studio/report',
    title: 'Studio report',
    description:
      'One report in Studio: pages of figures, charts and tables over a date range.',
    group: 'Studio',
    inRail: false
  },
  {
    // Opens only from a link, because it needs a document id.
    id: 'studio-deck',
    path: '/studio/deck',
    title: 'Studio deck',
    description:
      'One deck in Studio: slides with text, shapes, tables, charts and live report cards.',
    group: 'Studio',
    inRail: false
  },
  {
    // Opens only from a link, because it needs a document id.
    id: 'studio-dashboard',
    path: '/studio/dashboard',
    title: 'Studio TV dashboard',
    description:
      'One TV dashboard in Studio: a grid of live figures from pages you can open.',
    group: 'Studio',
    inRail: false
  },
  {
    // Opens only from a link, because it needs a document id.
    id: 'studio-tv',
    path: '/studio/tv',
    title: 'TV dashboard',
    description: 'One shared TV dashboard, drawn for an office screen.',
    group: 'Studio',
    inRail: false
  },
  {
    id: 'report-chat',
    path: '/lab/report-chat',
    title: 'Report chat',
    description:
      'A conversation that turns a question into a report draft, with that draft beside the chat.',
    group: 'Lab',
    pinned: true,
    icon: 'message-square'
  },
  {
    id: 'report-drafts',
    path: '/lab/report-drafts',
    title: 'Report drafts',
    description:
      'The report drafts your chats built, newest first. Nobody has checked their figures.',
    group: 'Lab',
    pinned: true,
    icon: 'file-text'
  },
  {
    // Opens only from a link, because it needs a draft id.
    id: 'report-draft',
    path: '/lab/report-draft',
    title: 'Report draft',
    description:
      'One report a chat drafted, opened from a link that names that draft.',
    group: 'Lab',
    inRail: false
  },
  {
    id: 'sales-tools-pricing-studio',
    path: '/sales-tools/pricing-studio',
    title: 'Pricing Studio',
    description:
      'A deal priced from the calculator, options compared side by side, and a proposal you can print.',
    group: 'Sales Tools',
    pinned: true,
    icon: 'bar-chart'
  },
  {
    id: 'platform-jobs',
    path: '/platform/jobs',
    title: 'Ingestion jobs',
    description: 'Every ingestion job, and the runs recorded for each one.',
    group: 'Platform',
    pinned: true,
    icon: 'play'
  },
  {
    id: 'platform-displays',
    path: '/platform/displays',
    title: 'Displays',
    description: 'The playlist each office TV rotates through.',
    group: 'Platform',
    pinned: true,
    icon: 'monitor'
  },
  {
    id: 'alerts-rules',
    path: '/alerts/rules',
    title: 'My alerts',
    description:
      'The Slack DMs and emails you asked for, which only you can see.',
    group: 'Alerts',
    pinned: true,
    icon: 'bell'
  },
  {
    id: 'account-change-log',
    path: '/cs-tools/account-change-log',
    takesDateRange: true,
    takesMerchants: true,
    title: 'Account change log',
    description:
      'Every change to a customer, an account, a payment method or a login: what changed, from what to what, and who changed it.',
    group: 'Customer support'
  },
  {
    id: 'follow-up-report',
    path: '/cs-tools/follow-up-report',
    takesMerchants: true,
    title: 'Follow-up report',
    keywords: ['follow ups', 'callbacks', 'overdue'],
    description:
      'Every follow-up merchant staff scheduled on an account, with how many are scheduled, overdue, completed and cancelled.',
    group: 'Customer support'
  },
  {
    id: 'payment-origin-share',
    path: '/cs-tools/payment-origin-share',
    title: 'Payment Origins',
    description:
      'Each payment origin’s share of paid and failed payments by month, for every merchant or one.',
    group: 'Customer support',
    takesMerchants: true
  },
  {
    id: 'inbound-messages',
    path: '/cs-tools/inbound-messages',
    takesDateRange: true,
    takesMerchants: true,
    title: 'Inbound messages',
    keywords: [
      'texts',
      'sms',
      'replies',
      'twilio',
      'customer messages',
      'response time',
      'agents',
      'queue'
    ],
    description:
      'The texts customers send in, by merchant, grouped into common replies such as already paid or will pay later, and how fast merchant agents answer them.',
    group: 'Customer support'
  },
  {
    id: 'chargeback-disputes',
    path: '/risk/chargeback-disputes',
    title: 'Chargeback disputes',
    description:
      'Explore card disputes, aging, unrepresented transactions and actual event timelines.',
    takesMerchants: true,
    takesDateRange: true,
    keywords: ['chargebacks', 'representment', 'aging', 'disputes'],
    group: 'Risk & chargebacks'
  }
])
